import {
  FiltersToolbar,
  flowsQuery,
  getFlowFilters,
  getFlowProperties,
  HeaderWithScrollables,
  localStorageKeyEntitiesPageFiltersValue,
  localStorageKeyEntitiesPageProperties,
  makeDynamicEntitiesPageStore,
  PageLayout,
  PageNames,
  SyncFiltersPropertiesProvider,
  useAssetsPerformanceGraphQLExportParameters,
  useEnvironment,
  useExportGridButton,
  useFilters,
  useGetFiltersToolbarHeight,
  useLocationTrackingForThings,
  useSidePeekPreference,
  useTableSort,
  PEOPLE_FLOW_ID,
  useSidePeekDrawerProps,
  useEvents,
  Events,
  hash,
  DataGridColumnConfigProvider,
} from '@diana/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { createPropertyToFilter } from './Support';
import { Content } from './Content';
import { HeaderLeft, HeaderRight } from './Headers';

export const ThingsPage = ({ peoplePage = false }: { peoplePage?: boolean }) => {
  const { flowOriginId: flowOriginIdFromParams } = useParams<{ flowOriginId: string }>();
  const flowOriginId = peoplePage ? PEOPLE_FLOW_ID : flowOriginIdFromParams;

  const queryClient = useQueryClient();

  const useEntitiesPageStore = makeDynamicEntitiesPageStore(flowOriginId!);
  const setDefaults = useEntitiesPageStore((store) => store.setDefaults);
  const setFlow = useEntitiesPageStore((store) => store.setFlow);
  const setAvailableFilters = useEntitiesPageStore((store) => store.setAvailableFilters);
  const setFilters = useEntitiesPageStore((store) => store.setFilters);
  const setFiltersValues = useEntitiesPageStore((store) => store.setFiltersValues);
  const resetData = useEntitiesPageStore((store) => store.resetData);
  const filters = useEntitiesPageStore((store) => store.filters);
  const filtersValues = useEntitiesPageStore((store) => store.filtersValues);
  const availableFilters = useEntitiesPageStore((store) => store.availableFilters);
  const defaultFiltersValuesAndPropertiesSet = useEntitiesPageStore(
    (store) => store.defaultFiltersValuesAndPropertiesSet,
  );

  const sidePeekDrawerProps = useSidePeekDrawerProps();
  const gridApiRef = useGridApiRef();
  const environment = useEnvironment();
  // Flow request
  const flowQueries = flowsQuery.getById({ id: flowOriginId! });
  const {
    data: flow,
    isLoading: isFlowLoading,
    refetch: refetchFlow,
  } = useQuery({
    queryKey: flowQueries.key,
    queryFn: flowQueries.fn,
    staleTime: Infinity,
    enabled: Boolean(flowOriginId ? flowOriginId : ''),
  });

  const { isLoadingUserSidePeekPreference, isUserSidePeekPreference } = useSidePeekPreference({
    flowOriginId: flowOriginId ?? '',
  });
  const showSidePeek =
    isUserSidePeekPreference ?? flow?.featureFlags?.isSingleScreenWorkUpdate ?? true;

  const { track } = useLocationTrackingForThings({ flow: flow || undefined });

  const propertyToFilter = useMemo(() => createPropertyToFilter(peoplePage), [peoplePage]);

  useEffect(() => {
    if (!flow) return;
    track();
    setFlow(flow);
    setAvailableFilters(
      getFlowFilters({ flow, propertyToFilter, onlyEnabled: false, onlyDefaultFilter: false }),
    );
    // eslint-disable-next-line
  }, [flow]);

  useEffect(() => {
    return () => resetData();
    // eslint-disable-next-line
  }, []);

  const supportedProperties = useMemo(() => {
    return flow
      ? getFlowProperties({
          flow,
          onlyEnabled: false,
          onlyDefaultFilter: false,
        })
      : [];
  }, [flow]);

  const { sortModel, onSortModelChange } = useTableSort({ supportedProperties });

  const queryParameters = useAssetsPerformanceGraphQLExportParameters({
    flow,
    filters: availableFilters,
    filtersValues,
    isExpandedFeatureEnabled: flow?.featureFlags?.isExpandedFeatureEnabled,
    sortModel,
  });

  const { setIsExportModalOpen, ExportModalComponent } = useExportGridButton({
    apiRef: gridApiRef,
    params: queryParameters,
    isEntity: true,
    exportConfigurations: [],
  });

  const { defaultData, onFilterChange, needToRenderAIFilter } = useFilters({
    flows: flow ? [flow] : [],
    createPropertyToFilter: () => createPropertyToFilter(peoplePage),
    filtersValues: filtersValues,
    setFiltersValues: setFiltersValues,
    availableFilters: availableFilters,
  });

  const dataGridStorageKey = `entityTable.thingstable-${flow?.entityType ?? ''}`;
  const height = useGetFiltersToolbarHeight(true);

  useEvents(
    (ctx) => {
      Events.AssetsImported.attach(ctx, async (data) => {
        if (data.updatedFlowOriginId === flowOriginId) {
          queryClient.removeQueries({ queryKey: flowQueries.key });
          refetchFlow().catch();
        }
      });
    },
    [flowOriginId, refetchFlow, queryClient.removeQueries, hash(flowQueries.key)],
  );

  return (
    <SyncFiltersPropertiesProvider
      filtersKey={localStorageKeyEntitiesPageFiltersValue(environment, flowOriginId!)}
      propertiesKey={localStorageKeyEntitiesPageProperties(environment, flowOriginId!)}
      subscribe={useEntitiesPageStore.subscribe}
      storeSetDefaults={setDefaults}
      defaultFiltersValuesAndPropertiesSet={
        defaultFiltersValuesAndPropertiesSet || (!isFlowLoading && !flow)
      }
    >
      <DataGridColumnConfigProvider
        flow={flow}
        updateKey={flowQueries.key}
        storageKey={dataGridStorageKey}
      >
        <PageLayout
          name={peoplePage ? PageNames.PEOPLE : PageNames.THINGS}
          title={flow?.displayName || '...'}
          isContentLoading={isFlowLoading || isLoadingUserSidePeekPreference}
          rightDrawer={sidePeekDrawerProps}
          filters={
            !isFlowLoading && !flow ? undefined : (
              <FiltersToolbar
                availableFilters={availableFilters}
                setAvailableFilters={setAvailableFilters}
                filters={filters}
                setFilters={setFilters}
                filtersValues={filtersValues}
                setFiltersValues={setFiltersValues}
                needToRenderAIFilter={needToRenderAIFilter}
                height={height}
                onFilterChange={onFilterChange}
                defaultData={defaultData}
                isContentLoading={isFlowLoading}
              />
            )
          }
          header={
            <>
              {flow ? (
                <HeaderWithScrollables
                  left={<HeaderLeft flow={flow} peoplePage={peoplePage} />}
                  right={
                    <HeaderRight
                      flow={flow}
                      handleCsvButtonClick={() => {
                        setIsExportModalOpen(true);
                      }}
                      isLoading={isFlowLoading}
                      openInSidePeek={showSidePeek}
                      peoplePage={peoplePage}
                    />
                  }
                />
              ) : null}
            </>
          }
        >
          {flow ? (
            <>
              <Content
                peoplePage={peoplePage}
                ref={gridApiRef}
                flow={flow}
                supportedProperties={supportedProperties}
                sortModel={sortModel}
                onSortModelChange={onSortModelChange}
                dataGridStorageKey={dataGridStorageKey}
                showSidePeek={showSidePeek}
              />
              {ExportModalComponent}
            </>
          ) : null}
        </PageLayout>
      </DataGridColumnConfigProvider>
    </SyncFiltersPropertiesProvider>
  );
};
